/**
 * Utility functions for handling artist data across components
 */

/**
 * Filters artists by role
 * @param {Array} artists - Array of artist objects
 * @param {string} role - Role to filter by ('mixer', 'producer', 'feat', etc.)
 * @returns {Array} Filtered array of artists with the specified role
 */
export const getArtistsByRole = (artists, role) => {
  if (!artists || artists.length === 0) return [];
  return artists.filter((artist) => artist.role === role);
};

/**
 * Gets mixer artists specifically
 * @param {Array} artists - Array of artist objects
 * @returns {Array} Array of mixer artists
 */
export const getMixerArtists = (artists) => {
  return getArtistsByRole(artists, 'mixer');
};

/**
 * Gets producer artists (including ID role)
 * @param {Array} artists - Array of artist objects
 * @returns {Array} Array of producer artists
 */
export const getProducerArtists = (artists) => {
  if (!artists || artists.length === 0) return [];
  return artists.filter(
    (artist) => artist.role === 'producer' || artist.role === 'ID'
  );
};

/**
 * Gets featured artists
 * @param {Array} artists - Array of artist objects
 * @returns {Array} Array of featured artists
 */
export const getFeaturedArtists = (artists) => {
  return getArtistsByRole(artists, 'feat');
};

/**
 * Gets artist names as a joined string
 * @param {Array} artists - Array of artist objects
 * @param {string} separator - Separator to use between names (default: ' & ')
 * @returns {string} Joined string of artist names
 */
export const getArtistNames = (artists, separator = ' & ') => {
  if (!artists || artists.length === 0) return '';
  return artists
    .map((artist) => artist.artist_meta?.name)
    .filter(Boolean)
    .join(separator);
};

/**
 * Gets mixer artist names as a joined string
 * @param {Array} artists - Array of artist objects
 * @returns {string} Joined string of mixer artist names
 */
export const getMixerArtistNames = (artists) => {
  const mixerArtists = getMixerArtists(artists);
  return getArtistNames(mixerArtists);
};

/**
 * Creates a track title with mixer information
 * @param {string} title - Track title
 * @param {Array} artists - Array of artist objects
 * @param {string} mixType - Mix type (e.g., 'Remix', 'Edit')
 * @returns {string} Formatted track title with mixer info
 */
export const getTrackTitleWithMixers = (title, artists, mixType) => {
  const mixerNames = getMixerArtistNames(artists);
  return `${title}${mixerNames ? ` (${mixerNames} ${mixType})` : ''}`;
};

/**
 * Separates artists by role (producers and featured)
 * @param {Array} artists - Array of artist objects
 * @returns {Object} Object with producers and feat arrays
 */
export const separateArtistsByRole = (artists) => {
  if (!artists || artists.length === 0) {
    return { producers: [], feat: [] };
  }

  const producers = getProducerArtists(artists);
  const feat = getFeaturedArtists(artists);

  return {
    producers: producers.length > 0 ? producers : artists,
    feat,
  };
};

/**
 * Gets artist URL for navigation
 * @param {Object} artist - Artist object
 * @returns {string} Artist URL path
 */
export const getArtistUrl = (artist) => {
  return `/artists/${artist.artist_meta?.id}`;
};

/**
 * Creates mixer artist display text for media sessions
 * @param {Array} artists - Array of artist objects
 * @returns {string} Formatted mixer artist string
 */
export const getMixerDisplayText = (artists) => {
  const mixerNames = getMixerArtistNames(artists);
  return mixerNames || 'Unknown';
};
